import React, { useState } from 'react';
import { CheckCircle2Icon, ClockIcon, ExternalLinkIcon, ImageIcon, UploadIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../../components/layout/AppShell';
import { SubNav, studioNav } from '../../../components/layout/SubNav';
import { Card, KeyValue, SectionHeading } from '../../../components/ui/Panels';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Avatar } from '../../../components/ui/Avatar';
import { Field, Input, Textarea } from '../../../components/ui/Field';
import { useSession } from '../../../contexts/SessionContext';
import { cuisines, cuisineLabel, dietaryCapabilities } from '../../../data/taxonomy';
import { cn } from '../../../utils/cn';
export function ChefProfileEditor() {
  const {
    chef
  } = useSession();
  const [headline, setHeadline] = useState(chef.headline);
  const [bio, setBio] = useState(chef.bio);
  const [languages, setLanguages] = useState(chef.languages.join(', '));
  const [cuisineIds, setCuisineIds] = useState<string[]>(chef.cuisineIds);
  const [capabilities, setCapabilities] = useState<string[]>(chef.dietaryCapabilities);
  const [saved, setSaved] = useState(false);
  const verifiedCount = chef.verifications.filter(v => v.status === 'verified').length;
  const toggle = (list: string[], setter: (v: string[]) => void, id: string) => setter(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  return <AppShell title="Profile & verification" subtitle={`${verifiedCount} of ${chef.verifications.length} checks complete`} actions={<Link to={`/chefs/${chef.slug}`} className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-2 text-[13px] font-medium text-ink-2 hover:text-ink" data-id="element-1290" data-mp="19utwnt47nvba">
          <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-1291" data-mp="he9ge3v10dyz" />
          View public profile
        </Link>} data-id="element-1289" data-mp="10629dya3hz6p">
      <SubNav items={studioNav} data-id="element-1292" data-mp="9d1wgnou6pqf" />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1293" data-mp="9c0j2ckclr5j">
        <div className="space-y-6" data-id="element-1294" data-mp="5bgjz3g7sdgr">
          <Card className="p-5" data-id="element-1295" data-mp="5af8wyz77qz1">
            <SectionHeading level={2} title="How you introduce yourself" data-id="element-1296" data-mp="j465l6rtzgdc" />
            <div className="mt-4 flex flex-wrap items-center gap-4" data-id="element-1297" data-mp="e4jup3zh6c7p">
              <Avatar name={chef.name} src={chef.photo} size="xl" data-id="element-1298" data-mp="e5bqaet2o9xz" />
              <div data-id="element-1299" data-mp="3mlemnoeqic70">
                <Button type="button" variant="outline" size="sm" data-id="element-1300" data-mp="3mkvf91mpn678">
                  <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1301" data-mp="3ijjo2ro6ucwx" />
                  Change portrait
                </Button>
                <p className="mt-1.5 text-[12px] text-ink-3" data-id="element-1302" data-mp="eq6mnvsnvz1">
                  Guests scan photos before words. A clear face beats a plated dish here.
                </p>
              </div>
            </div>

            <Field label="Headline" id="cp-headline" hint="one line, shown under your name in search" className="mt-5" data-id="element-1303" data-mp="1bqh7ckr6bnbq">
              <Input id="cp-headline" value={headline} onChange={e => setHeadline(e.target.value)} maxLength={90} data-id="element-1304" data-mp="16vlclcujylux" />
            </Field>

            <Field label="About you" id="cp-bio" hint={`${bio.length} characters`} className="mt-4" data-id="element-1305" data-mp="2tq72w901dww7">
              <Textarea id="cp-bio" rows={6} value={bio} onChange={e => setBio(e.target.value)} data-id="element-1306" data-mp="2fqxpfh37tgvx" />
            </Field>

            <Field label="Languages you cook and message in" id="cp-lang" className="mt-4" data-id="element-1307" data-mp="2kq1wa12o32j3">
              <Input id="cp-lang" value={languages} onChange={e => setLanguages(e.target.value)} data-id="element-1308" data-mp="38g4k24zjtmbw" />
            </Field>

            <div className="mt-5" data-id="element-1309" data-mp="3cfn4kzngjve7">
              <p className="mb-2 text-[13px] font-semibold text-ink" data-id="element-1310" data-mp="2yp9xezie4kpx">Cuisines</p>
              <div className="flex flex-wrap gap-1.5" data-id="element-1311" data-mp="2yn78yjwvyznb">
                {cuisines.map(cuisine => {
                const on = cuisineIds.includes(cuisine.id);
                return <button key={cuisine.id} type="button" aria-pressed={on} onClick={() => toggle(cuisineIds, setCuisineIds, cuisine.id)} className={cn('rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out', on ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-1312" data-mp="3rbmbi6ytzphr">
                      {cuisine.label}
                    </button>;
              })}
              </div>
            </div>

            <div className="mt-5" data-id="element-1313" data-mp="2m9lmo7e4i41t">
              <p className="mb-1 text-[13px] font-semibold text-ink" data-id="element-1314" data-mp="2hg8z80l6k3an">What you can cook for</p>
              <p className="mb-2 text-[12px] text-ink-3" data-id="element-1315" data-mp="2chxnfkbbrips">
                Only tick what you can genuinely handle — guests filter on these and allergies are not a place to stretch.
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-1316" data-mp="356mtlxr8me8e">
                {dietaryCapabilities.map(cap => {
                const on = capabilities.includes(cap);
                return <button key={cap} type="button" aria-pressed={on} onClick={() => toggle(capabilities, setCapabilities, cap)} className={cn('rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out', on ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-1317" data-mp="3hgcg19znjwja">
                      {cap}
                    </button>;
              })}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3" data-id="element-1318" data-mp="e1ovr2y6wqgi">
              <Button type="button" variant="action" onClick={() => {
              setSaved(true);
              window.setTimeout(() => setSaved(false), 2400);
            }} data-id="element-1319" data-mp="i2s99isrmaek">
                Save profile
              </Button>
              {saved && <span role="status" className="text-[13px] font-medium text-success" data-id="element-1320" data-mp="2f00ltbzppl8l">
                  Saved
                </span>}
            </div>
          </Card>

          <Card className="p-5" data-id="element-1321" data-mp="2o1gunfavpfap">
            <div className="flex items-center gap-2" data-id="element-1322" data-mp="32ro1exrix9vk">
              <ImageIcon className="h-4 w-4 text-ink-2" aria-hidden="true" data-id="element-1323" data-mp="32q4lx9doz2uu" />
              <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1324" data-mp="2xtea4y8x6tf8">Gallery</h2>
            </div>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1325" data-mp="3brb55q7q6rrs">
              Dishes sell the booking; one process shot and one portrait make it feel real.
            </p>
            <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-1326" data-mp="1heczxl0vulbb">
              {chef.gallery.map(item => <li key={item.id} className="group relative overflow-hidden rounded-lg border border-hairline" data-id="element-1327" data-mp="1cfrvf9ellfh2">
                  <img src={item.url} alt={item.caption} className="aspect-square w-full object-cover" loading="lazy" data-id="element-1328" data-mp="3o2bznegmc3zl" />
                  <span className="absolute left-2 top-2 rounded-full bg-surface/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] text-ink-2" data-id="element-1329" data-mp="3t0dibya4u1aj">
                    {item.tag}
                  </span>
                </li>)}
              <li data-id="element-1330" data-mp="35cdnon9p9qqo">
                <button type="button" className="flex aspect-square w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-hairline text-ink-3 transition-colors duration-150 ease-out hover:border-primary/50 hover:text-ink" data-id="element-1331" data-mp="35bugo1ll8mrf">
                  <UploadIcon className="h-5 w-5" aria-hidden="true" data-id="element-1332" data-mp="30dsomr66pd3t" />
                  <span className="text-[12px] font-medium" data-id="element-1333" data-mp="ujjah20kglgh">Add photo</span>
                </button>
              </li>
            </ul>
          </Card>
        </div>

        <aside className="space-y-4" data-id="element-1334" data-mp="yjjvx6m31wx1">
          <Card className="p-5" data-id="element-1335" data-mp="3ii8dczzm2opz">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1336" data-mp="3ih5kwnmn4e7y">Verification</h2>
            <ul className="mt-3 space-y-3" data-id="element-1337" data-mp="3djds7re68o2p">
              {chef.verifications.map(item => <li key={item.kind} className="flex items-start gap-2.5" data-id="element-1338" data-mp="38q2klo1jc39q">
                  <span className={cn('mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full', item.status === 'verified' ? 'bg-success text-white' : 'bg-oat text-ink-3')} aria-hidden="true" data-id="element-1339" data-mp="33rr6v284d8m8">
                    {item.status === 'verified' ? <CheckCircle2Icon className="h-3.5 w-3.5" data-id="element-1340" data-mp="2kupzxbv08yfn" /> : <ClockIcon className="h-3.5 w-3.5" data-id="element-1341" data-mp="o1wnl624z0kx" />}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-1342" data-mp="j2bomvmyus87">
                    <span className="block text-sm font-medium text-ink" data-id="element-1343" data-mp="j0qsx2ox138r">{item.label}</span>
                    <span className="block text-[12px] text-ink-3" data-id="element-1344" data-mp="rhhpshpxa5p9">{item.detail}</span>
                  </span>
                  {item.status !== 'verified' && <Badge tone={item.status === 'pending' ? 'accent' : 'neutral'} data-id="element-1345" data-mp="10cxgipvds4qm">{item.status}</Badge>}
                </li>)}
            </ul>
            <Button type="button" variant="outline" size="sm" full className="mt-4" data-id="element-1346" data-mp="8l80ey9klspy">
              <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1347" data-mp="cm1q9x6kphlh" />
              Upload a document
            </Button>
            <p className="mt-2 text-[12px] leading-relaxed text-ink-3" data-id="element-1348" data-mp="hl4w3judnrui">
              Food hygiene certificates are checked by a person, usually within two working days.
            </p>
          </Card>

          <Card className="p-5" data-id="element-1349" data-mp="1t1coisg3hmsg">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1350" data-mp="1t0bemratf6cb">Your listing at a glance</h2>
            <dl className="mt-3" data-id="element-1351" data-mp="1y1h8isg7oznf">
              <KeyValue label="Base" value={chef.neighbourhood} data-id="element-1352" data-mp="ml55qrgg9w09" />
              <KeyValue label="Years cooking" value={`${chef.yearsPro}`} data-id="element-1353" data-mp="misqwn5zo1rf" />
              <KeyValue label="Cuisines" value={cuisineIds.map(cuisineLabel).join(', ')} data-id="element-1354" data-mp="rhvo5lfq93ou" />
              <KeyValue label="Rating" value={`${chef.rating.toFixed(1)} · ${chef.reviewCount} reviews`} data-id="element-1355" data-mp="wayi3uof2f5k" />
              <KeyValue label="Completed" value={`${chef.bookingsCompleted} bookings`} data-id="element-1356" data-mp="10b8np68ar3jw" />
            </dl>
          </Card>
        </aside>
      </div>
    </AppShell>;
}